import {
    ClientAttributesService,
    HashStore,
    LockManager,
    PulseResponse,
    StateService,
} from '@pulse/browser-shared';
import { ClientAttributes, ExperimentInfo, SettingsConfigurations } from '@pulse/common';
import { Observable, Observer, Subject, combineLatest } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { FirstCall, SettingsResponse } from '../../models';
import { CacheService } from '../cache.service';
import { ExperimentDebugService } from '../experiment-debug.service';
import { IsTestService } from '../is-test.service';
import { QueueService } from '../queue.service';
import { UsageTrackerService } from '../usage-tracker.service';
import { UserRoleService } from '../user-role.service';
import { SettingsFetchHelper } from './settings-fetch.helper';
import { SettingsHelper } from './settings.helper';

export class SettingsObserver {
    private observers = new Map<string, Array<Observer<any>>>();
    private subjects = new Map<string, Observable<any>>();
    private revalidate = new Subject<any>();
    private firstCallState: FirstCall = {
        firstCallDone: false,
        defaultReturned: false,
    };

    private lock: string[] = [];
    private syncIndexedDB: Promise<boolean>;

    constructor(
        private settingsHelper: SettingsHelper,
        private settingsFetchHelper: SettingsFetchHelper,
        private cacheService: CacheService,
        private config: SettingsConfigurations,
        private usageTrackerService: UsageTrackerService,
        private experimentDebugService: ExperimentDebugService,
        private hashStore: HashStore,
        private lockManager: LockManager,
        private queueService: QueueService,
        private userRoleService: UserRoleService,
        private isTestService: IsTestService,
        private stateService: StateService,
        private clientAttributesService: ClientAttributesService,
    ) {
        this.userRoleService.setFromQueryParams();
        this.isTestService.setFromQueryParams();
        this.getPrefetchSettings();

        combineLatest({
            state: this.stateService.getState$().pipe(startWith(this.stateService.getState())),
            userRole: this.userRoleService
                .getValue$()
                .pipe(startWith(this.userRoleService.getValue())),
            isTest: this.isTestService.getValue$().pipe(startWith(this.isTestService.getValue())),
        }).subscribe({
            next: ({ state, userRole, isTest }) => {
                this.refreshStateProps({
                    state: state as Record<string, unknown>,
                    userRole: userRole || '',
                    isTest: isTest || '',
                });
            },
        });

        this.syncIndexedDB = this.cacheService.updateInMemorySettings();
    }

    observe(
        tag: string,
        settingName: string,
        defaultValue: any,
        remoteSettingEnabled: boolean,
        attributes?: ClientAttributes,
    ): Observable<any> {
        return new Observable<any>((observer) => {
            this.observers.set(settingName, [...(this.observers.get(settingName) || []), observer]);
            const existingObservable = this.subjects.get(settingName);
            const observable = existingObservable
                ? existingObservable
                : this.getSettings(
                      tag,
                      settingName,
                      defaultValue,
                      remoteSettingEnabled,
                      attributes,
                  );
            const subscription = observable.subscribe(observer);

            return () => {
                const value = this.observers.get(settingName)?.filter((o) => o !== observer) || [];

                if (value.length === 0) {
                    this.observers.delete(settingName);
                } else {
                    this.observers.set(settingName, value);
                }

                subscription.unsubscribe();
            };
        });
    }

    getSettings(
        tag: string,
        settingName: string,
        defaultValue: any,
        remoteSettingEnabled: boolean,
        attributes?: ClientAttributes,
    ): Observable<any> {
        return new Observable<any>((observer) => {
            this.get(tag, settingName, defaultValue, remoteSettingEnabled, attributes).then(
                (value) => {
                    observer.next(value);
                },
            );

            const subscription = this.revalidate.subscribe(() => {
                this.get(tag, settingName, defaultValue, remoteSettingEnabled, attributes).then(
                    (value) => {
                        observer.next(value);
                    },
                );
            });

            return () => {
                subscription.unsubscribe();
            };
        });
    }

    async get<T = any>(
        tag: string,
        settingName: string,
        defaults?: any,
        remoteSettingEnabled = true,
        attributes?: ClientAttributes,
    ): Promise<T> {
        const defaultValue = defaults !== undefined ? defaults : this.config.defaults[settingName];

        if (
            this.settingsHelper.shouldReturnDefaultValue(
                remoteSettingEnabled,
                this.firstCallState,
                settingName,
                tag,
                this.cacheService.getCachedSettings(settingName),
            )
        ) {
            return this.usageTrackerService.trackUsage(tag, settingName, defaultValue);
        }

        // clear cache if experiment debug mode is activated
        const debugExperiment = this.experimentDebugService.getDebugExperiment();
        const debugExperimentKey = debugExperiment
            ? `${this.config.app}:${debugExperiment.id}:${debugExperiment.variant}`
            : null;

        if (this.hashStore.changed('debug-experiment', debugExperimentKey)) {
            this.cacheService.clearCache();
        }

        let response: PulseResponse<SettingsResponse>;
        // Wait until sync is ended
        await this.syncIndexedDB;

        const cache = this.cacheService.getCachedSettings(settingName);
        const emptyForTags: string[] =
            typeof cache?.cachedSetting['emptyForTags'] === 'string'
                ? cache.cachedSetting['emptyForTags'].split(',')
                : [];
        const attributesHashFromCache = cache?.cachedSetting?.['attributesHash'];
        const userRole = this.userRoleService.getValue() || '';
        const isTest = this.isTestService.getValue();
        const attributesHash = this.settingsHelper.getAttributesHash({
            tag,
            userRole: String(userRole ?? ''),
            isTest: String(isTest ?? ''),
            userId: String(this.stateService.getState()['user_id'] ?? ''),
            tierId: String(this.stateService.getState()['tier_id'] ?? ''),
            deviceLastTierId: String(this.stateService.getState()['device_last_tier_id'] ?? ''),
            debugExperiment: debugExperiment
                ? `${debugExperiment.id}:${debugExperiment.variant}`
                : '',
            attributes: attributes || {},
            appVersion: this.config.app_version || '0',
        });

        if (cache?.cachedSetting && attributesHashFromCache !== attributesHash) {
            cache.timedOut = true;
        }

        if (
            !cache?.timedOut &&
            cache?.cachedSetting[settingName] === undefined &&
            emptyForTags.includes(tag)
        ) {
            return this.usageTrackerService.trackUsage(tag, settingName, defaultValue);
        }

        if (
            cache !== undefined &&
            cache.cachedSetting &&
            !cache.timedOut &&
            cache.cachedSetting[settingName] !== undefined
        ) {
            return this.usageTrackerService.trackUsage(
                tag,
                settingName,
                cache.cachedSetting[settingName],
            );
        } else {
            if (this.lock.includes(attributesHash)) {
                await this.queueService.waitQueueOpen(
                    attributesHash,
                    this.config.timeout,
                    this.lock,
                );

                return this.getFromCacheAndReturn({
                    settingName,
                    settingDefaultValue: defaultValue,
                    tag,
                    userRole,
                    isTest,
                    debugExperiment,
                    attributes,
                });
            } else {
                this.lock.push(attributesHash);
            }

            const locked = await this.lockManager.locked(attributesHash);

            if (locked) {
                await this.queueService.waitQueueOpen(
                    attributesHash,
                    this.config.timeout,
                    this.lock,
                );

                return await this.getFromCacheAndReturn({
                    settingName,
                    settingDefaultValue: defaultValue,
                    tag,
                    userRole,
                    isTest,
                    debugExperiment,
                    attributes,
                });
            } else {
                if (!this.lock.includes(attributesHash)) {
                    this.lock.push(attributesHash);
                }

                const settingLock = await this.lockManager.request(
                    attributesHash,
                    this.config.timeout,
                );
                response = await this.settingsFetchHelper.fetchSettings(
                    tag,
                    userRole,
                    isTest,
                    settingName,
                    emptyForTags,
                    attributes || {},
                    this.config,
                    String(this.stateService.getState()['user_id'] ?? ''),
                    String(this.stateService.getState()['tier_id'] ?? ''),
                    String(this.stateService.getState()['device_last_tier_id'] ?? ''),
                    this.firstCallState,
                    this.mutateFirstCallState,
                    attributesHash,
                );
                const settings = response?.body?.settings;
                let value = settings && settings[settingName];

                if (value === undefined) {
                    value = cache?.cachedSetting[settingName] || defaultValue;
                }

                if (this.lock.includes(attributesHash)) {
                    this.lock.splice(this.lock.indexOf(attributesHash), 1);
                }

                if (settingLock) {
                    this.lockManager.release(settingLock);
                }

                return this.usageTrackerService.trackUsage(tag, settingName, value);
            }
        }
    }

    private getFromCacheAndReturn({
        settingName,
        settingDefaultValue,
        tag,
        userRole,
        isTest,
        debugExperiment,
        attributes,
    }: {
        settingName: string;
        settingDefaultValue: any;
        tag: string;
        userRole: string;
        isTest: string | null;
        debugExperiment: ExperimentInfo | null;
        attributes: ClientAttributes | undefined;
    }) {
        const attributesHash = this.settingsHelper.getAttributesHash({
            tag,
            userRole: String(userRole ?? ''),
            isTest: String(isTest ?? ''),
            userId: String(this.stateService.getState()['user_id'] ?? ''),
            tierId: String(this.stateService.getState()['tier_id'] ?? ''),
            deviceLastTierId: String(this.stateService.getState()['device_last_tier_id'] ?? ''),
            debugExperiment: debugExperiment
                ? `${debugExperiment.id}:${debugExperiment.variant}`
                : '',
            attributes: attributes || {},
            appVersion: this.config.app_version || '0',
        });

        if (this.lock.includes(attributesHash)) {
            this.lock.splice(this.lock.indexOf(attributesHash), 1);
        }

        const cache = this.cacheService.getCachedSettings(settingName);
        let settingValue;

        if (cache?.cachedSetting && cache?.cachedSetting[settingName] !== undefined) {
            settingValue = cache?.cachedSetting[settingName];
        } else {
            const emptyForTags: string[] =
                typeof cache?.cachedSetting['emptyForTags'] === 'string'
                    ? cache.cachedSetting['emptyForTags'].split(',')
                    : [];

            if (!emptyForTags.includes(tag)) {
                emptyForTags.push(tag);
            }

            this.cacheService.cacheSettings(
                { [settingName]: undefined },
                tag,
                emptyForTags,
                attributesHash,
            );
        }

        settingValue = settingValue !== undefined ? settingValue : settingDefaultValue;

        return this.usageTrackerService.trackUsage(tag, settingName, settingValue);
    }

    private refreshStateProps({
        state,
        userRole,
        isTest,
    }: {
        state: Record<string, unknown>;
        userRole: string;
        isTest: string;
    }) {
        const userId = state['user_id'] as string;
        const tierId = state['tier_id'] as string;
        const deviceLastTierId = state['device_last_tier_id'] as string;

        const changed =
            this.hashStore.changed('user_id', userId) ||
            this.hashStore.changed('tier_id', tierId) ||
            this.hashStore.changed('device_last_tier_id', deviceLastTierId) ||
            this.hashStore.changed('user_role', userRole) ||
            this.hashStore.changed('is_test', isTest);

        if (changed) {
            this.revalidate.next(true);
        }
    }

    getPrefetchSettings() {
        let prefetchTags: string | null = '';
        const pulseScriptUrl = this.clientAttributesService.getPulseScript();

        if (pulseScriptUrl) {
            const searchParams = new URLSearchParams(pulseScriptUrl.search);
            prefetchTags = searchParams.get('prefetch');
        }

        if (prefetchTags && pulseScriptUrl) {
            const attributes = this.clientAttributesService.getAttributes();
            prefetchTags.split(',').forEach((tag) => {
                this.get(
                    tag,
                    `${this.config.storagePrefix}${tag}_prefetched`,
                    {},
                    true,
                    attributes,
                );
            });
        }
    }

    mutateFirstCallState(prop: keyof FirstCall, value: boolean, state: FirstCall) {
        if (state) {
            state[prop] = value;
        } else {
            this.firstCallState[prop] = value;
        }
    }
}
